import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Weight, Zap, Wrench, Shield, TrendingUp, Award } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import t300Img from "@/assets/robots/t300-hero.jpeg";
import followModeImg from "@/assets/robots/follow-mode-demo.webp";
import actionVideo from "@/assets/robots/t600-t300-action.mov";

const T300Series = () => {
  const specifications = [
    {
      icon: <Weight className="w-5 h-5" />,
      label: "Load Capacity",
      value: "300kg Payload"
    },
    {
      icon: <Zap className="w-5 h-5" />,
      label: "Runtime",
      value: "72+ Hours Non-stop"
    },
    {
      icon: <Wrench className="w-5 h-5" />,
      label: "Accessories",
      value: "5 Add-on Options"
    },
    {
      icon: <Award className="w-5 h-5" />,
      label: "Award",
      value: "Red Dot Winner 2025"
    }
  ];

  const keyFeatures = [
    {
      icon: <Award className="w-6 h-6" />,
      title: "Red Dot Design Award 2025",
      description: "Internationally recognized for outstanding industrial design, combining functionality with aesthetic excellence in material handling robotics."
    },
    {
      icon: <Weight className="w-6 h-6" />,
      title: "300kg Load Capacity",
      description: "Industry-leading payload capacity enables heavy-duty material transport, reducing multiple trips and maximizing operational efficiency."
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Intelligent Follow Mode",
      description: "Automatically follows workers with hands-free operation, reducing physical strain with blue ground projection for enhanced safety awareness."
    },
    {
      icon: <Wrench className="w-6 h-6" />,
      title: "5 Add-On Accessories",
      description: "Versatile configuration with T300 Base, Tray, Lifting, Towing & Conveyor options to adapt to diverse industrial applications."
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Power-Assist Mode",
      description: "Manual push with motorized assistance for effortless maneuvering of heavy loads, reducing worker fatigue and injury risk."
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "ISO 3691-4 Safety Compliant",
      description: "Industrial-grade safety standards certification ensures secure operation in demanding manufacturing and logistics environments."
    }
  ];

  const accessories = [
    {
      name: "T300 Base",
      description: "Standard platform for custom attachments and flexible material handling applications."
    },
    {
      name: "Tray Configuration",
      description: "Multiple tray levels for organized transport of parts, tools, and materials."
    },
    {
      name: "Lifting Module",
      description: "Automated lifting mechanism for height-adjustable material delivery and ergonomic positioning."
    },
    {
      name: "Towing Attachment",
      description: "Pull carts and trailers for extended payload capacity in warehouse operations."
    },
    {
      name: "Conveyor System",
      description: "Integrated conveyor belt for seamless loading/unloading at workstations."
    }
  ];

  const applications = [
    {
      title: "Manufacturing",
      description: "Parts delivery to assembly lines, tool transport between workstations, and material handling in smart factories with follow-mode assistance."
    },
    {
      title: "Warehousing",
      description: "Heavy load transport, order picking support, inventory management, and goods-to-person delivery with autonomous navigation."
    },
    {
      title: "Logistics Centers",
      description: "Parcel sorting assistance, pallet movement, loading dock operations, and cross-docking with 300kg capacity."
    },
    {
      title: "Automotive Industry",
      description: "Engine and transmission delivery, tire transport, parts sequencing, and just-in-time material delivery to production lines."
    },
    {
      title: "Aerospace Manufacturing",
      description: "Heavy component transport, tool delivery, parts staging for aircraft assembly with precision positioning."
    },
    {
      title: "Food Processing",
      description: "Raw material transport, finished goods handling, and packaging material delivery in FDA-compliant facilities."
    }
  ];

  return (
    <>
      <SEO 
        title="PUDU T300 Series - 300kg Industrial Delivery Robot | Red Dot Winner 2025"
        description="Award-winning industrial robot with 300kg capacity, intelligent follow mode, 72+ hours runtime, and 5 versatile accessories. Red Dot Design Award Winner 2025."
        keywords="PUDU T300, industrial robot, material handling robot, AGV, warehouse automation, manufacturing robot, Red Dot Award"
      />
      <Navigation />
      
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
        <div className="container mx-auto px-4 pt-12 pb-8 md:pt-16">
          <Button asChild variant="ghost" className="mb-6">
            <Link to="/robots">
              <ArrowLeft className="mr-2 w-4 h-4" />
              Back to Robots
            </Link>
          </Button>

          {/* Hero Section */}
          <div className="grid lg:grid-cols-2 gap-8 mb-12">
            <div className="relative bg-white/5 rounded-lg p-8 flex items-center justify-center">
              <img 
                src={t300Img} 
                alt="PUDU T300 Series - Industrial Delivery Robot" 
                className="w-full h-auto max-h-[600px] object-contain"
              />
              <Badge className="absolute top-4 right-4 bg-destructive text-destructive-foreground font-bold text-lg px-4 py-2">
                RED DOT 2025
              </Badge>
            </div>

            <div className="flex flex-col justify-center space-y-6">
              <div>
                <p className="text-primary font-semibold mb-2">Industrial Delivery Robot</p>
                <h1 className="font-orbitron font-bold text-2xl md:text-4xl lg:text-5xl mb-4 break-words">PUDU T300 Series</h1>
                <p className="text-base md:text-lg lg:text-xl text-muted-foreground mb-6 leading-relaxed">
                  Award-winning industrial robot with 300kg payload capacity, intelligent follow mode, and 5 versatile add-on accessories. Red Dot Design Award Winner 2025.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {specifications.map((spec, idx) => (
                  <Card key={idx} className="glass-effect border-[hsl(var(--line))]">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2 text-primary">
                        {spec.icon}
                        <p className="text-xs font-semibold">{spec.label}</p>
                      </div>
                      <p className="text-sm font-bold">{spec.value}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  asChild 
                  size="lg"
                  className="glow-gold flex-1"
                >
                  <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                    Book a Demo
                  </a>
                </Button>
                <Button 
                  asChild
                  size="lg"
                  variant="outline"
                  className="flex-1"
                >
                  <a href="https://myascentium.com/ApplyNow/CommCredit?v=b106671d-0a80-40ca-aea6-5c888e426df6&s=dca75d8e-7184-4ed5-a7d0-f35ed9e35e86" target="_blank" rel="noopener noreferrer">
                    Apply for Financing
                  </a>
                </Button>
              </div>
            </div>
          </div>

          {/* Detailed Information Tabs */}
          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="flex w-full mb-8 overflow-x-auto scrollbar-hide">
              <TabsTrigger value="overview" className="flex-shrink-0 text-xs sm:text-sm px-3 py-2">Overview</TabsTrigger>
              <TabsTrigger value="specifications" className="flex-shrink-0 text-xs sm:text-sm px-3 py-2">Specs</TabsTrigger>
              <TabsTrigger value="applications" className="flex-shrink-0 text-xs sm:text-sm px-3 py-2">Applications</TabsTrigger>
              <TabsTrigger value="action" className="flex-shrink-0 text-xs sm:text-sm px-3 py-2">See In Action</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-8">
              <Card className="glass-effect border-[hsl(var(--line))]">
                <CardHeader>
                  <CardTitle className="font-orbitron text-2xl">Award-Winning Industrial Innovation</CardTitle>
                  <CardDescription>
                    Red Dot Design Award 2025 winner combining power, versatility, and intelligent operation
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    The PUDU T300 Series represents a breakthrough in industrial material handling, earning the prestigious Red Dot Design Award 2025 for its innovative approach to heavy-duty automation. With an industry-leading 300kg payload capacity, the T300 handles the most demanding transport tasks while maintaining the agility needed for modern manufacturing environments.
                  </p>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    The intelligent follow mode revolutionizes worker productivity by automatically following operators with hands-free operation. Blue ground projection provides enhanced safety awareness, while the power-assist mode enables manual maneuvering with motorized assistance for effortless control of heavy loads.
                  </p>
                  <p className="text-muted-foreground leading-relaxed">
                    With 72+ hours of continuous operation and 5 versatile add-on accessories, the T300 adapts to diverse applications from assembly line support to warehouse logistics. The flexible VSLAM+ deployment requires no markers, while 60cm superior mobility navigates tight industrial spaces with ease.
                  </p>
                </CardContent>
              </Card>

              <div>
                <h2 className="font-orbitron text-2xl font-bold mb-6">Key Features</h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {keyFeatures.map((feature, idx) => (
                    <Card key={idx} className="glass-effect border-[hsl(var(--line))] hover:scale-105 transition-all duration-300">
                      <CardContent className="p-6">
                        <div className="text-primary mb-3">{feature.icon}</div>
                        <h3 className="font-bold text-lg mb-2">{feature.title}</h3>
                        <p className="text-sm text-muted-foreground">{feature.description}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              <div>
                <h2 className="font-orbitron text-2xl font-bold mb-6">5 Add-On Accessories</h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {accessories.map((accessory, idx) => (
                    <Card key={idx} className="glass-effect border-[hsl(var(--line))]">
                      <CardContent className="p-4">
                        <h3 className="font-bold mb-2 text-primary">{accessory.name}</h3>
                        <p className="text-sm text-muted-foreground">{accessory.description}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              <Card className="glass-effect border-[hsl(var(--line))] bg-primary/5">
                <CardContent className="p-8">
                  <h3 className="font-orbitron text-xl font-bold mb-4">ROI Impact</h3>
                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="text-center">
                      <p className="text-3xl font-bold text-primary mb-2">60%</p>
                      <p className="text-sm">Labor Cost Reduction</p>
                    </div>
                    <div className="text-center">
                      <p className="text-3xl font-bold text-primary mb-2">72+</p>
                      <p className="text-sm">Hours Non-stop Operation</p>
                    </div>
                    <div className="text-center">
                      <p className="text-3xl font-bold text-primary mb-2">300kg</p>
                      <p className="text-sm">Maximum Payload</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="specifications" className="space-y-6">
              <Card className="glass-effect border-[hsl(var(--line))]">
                <CardHeader>
                  <CardTitle className="font-orbitron text-2xl">Technical Specifications</CardTitle>
                  <CardDescription>Detailed technical capabilities of the T300 Series</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div>
                      <h3 className="font-bold text-lg mb-4 text-primary">Performance</h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <p className="text-sm"><span className="font-semibold">Payload Capacity:</span> 300kg maximum</p>
                          <p className="text-sm"><span className="font-semibold">Operation Time:</span> 72+ hours continuous</p>
                          <p className="text-sm"><span className="font-semibold">Speed:</span> Adaptive speed control</p>
                        </div>
                        <div className="space-y-2">
                          <p className="text-sm"><span className="font-semibold">Navigation:</span> VSLAM+ marker-less</p>
                          <p className="text-sm"><span className="font-semibold">Mobility:</span> 60cm passage width</p>
                          <p className="text-sm"><span className="font-semibold">Modes:</span> Auto, Follow, Power-Assist</p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="font-bold text-lg mb-4 text-primary">Operating Modes</h3>
                      <div className="space-y-3">
                        <div className="p-4 rounded-lg bg-white/5 border border-[hsl(var(--line))]">
                          <h4 className="font-semibold mb-1">Auto-Delivery Mode</h4>
                          <p className="text-sm text-muted-foreground">Fully autonomous point-to-point delivery with obstacle avoidance</p>
                        </div>
                        <div className="p-4 rounded-lg bg-white/5 border border-[hsl(var(--line))]">
                          <h4 className="font-semibold mb-1">Intelligent Follow Mode</h4>
                          <p className="text-sm text-muted-foreground">Hands-free worker following with blue safety projection</p>
                        </div>
                        <div className="p-4 rounded-lg bg-white/5 border border-[hsl(var(--line))]">
                          <h4 className="font-semibold mb-1">Power-Assist Mode</h4>
                          <p className="text-sm text-muted-foreground">Manual control with motorized assistance for heavy loads</p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="font-bold text-lg mb-4 text-primary">Safety & Compliance</h3>
                      <div className="space-y-2">
                        <p className="text-sm">• <span className="font-semibold">Certification:</span> ISO 3691-4 Safety Compliant</p>
                        <p className="text-sm">• <span className="font-semibold">Ground Projection:</span> Blue safety awareness system</p>
                        <p className="text-sm">• <span className="font-semibold">Sensors:</span> 3D obstacle detection and avoidance</p>
                        <p className="text-sm">• <span className="font-semibold">Emergency Stop:</span> Multiple safety cutoff points</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="applications" className="space-y-6">
              <Card className="glass-effect border-[hsl(var(--line))]">
                <CardHeader>
                  <CardTitle className="font-orbitron text-2xl">Industry Applications</CardTitle>
                  <CardDescription>
                    Transform industrial operations with versatile automation
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-6">
                    {applications.map((app, idx) => (
                      <div key={idx} className="space-y-2 p-4 rounded-lg bg-white/5 border border-[hsl(var(--line))]">
                        <h3 className="font-bold text-lg text-primary">{app.title}</h3>
                        <p className="text-sm text-muted-foreground">{app.description}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="glass-effect border-[hsl(var(--line))]">
                <CardHeader>
                  <CardTitle className="font-orbitron text-2xl">Intelligent Follow Mode</CardTitle>
                  <CardDescription>
                    Hands-free worker assistance with blue safety projection
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-8 items-center">
                    <div>
                      <img 
                        src={followModeImg} 
                        alt="T300 Series Follow Mode in Action" 
                        className="w-full object-contain rounded-lg"
                      />
                    </div>
                    <div className="space-y-4">
                      <p className="text-muted-foreground leading-relaxed">
                        The T300's intelligent follow mode revolutionizes material handling by automatically tracking and following workers throughout the facility. This hands-free operation eliminates the physical strain of pushing heavy loads while maintaining optimal safety.
                      </p>
                      <ul className="space-y-3">
                        <li className="flex items-start gap-3">
                          <span className="text-primary text-xl">✓</span>
                          <span>Automatic worker tracking with real-time positioning</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <span className="text-primary text-xl">✓</span>
                          <span>Blue ground projection for enhanced safety awareness</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <span className="text-primary text-xl">✓</span>
                          <span>Reduces physical strain and worker fatigue by 60%</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <span className="text-primary text-xl">✓</span>
                          <span>Maintains safe following distance automatically</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <span className="text-primary text-xl">✓</span>
                          <span>Seamless switching between auto and follow modes</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="glass-effect border-[hsl(var(--line))] bg-primary/5">
                <CardContent className="p-8 text-center">
                  <h3 className="font-orbitron text-2xl font-bold mb-4">Ready to Transform Your Operations?</h3>
                  <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                    Join industry leaders using the award-winning T300 Series to revolutionize material handling and boost productivity.
                  </p>
                  <Button asChild size="lg" className="glow-gold">
                    <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                      Schedule Your Demo Today
                    </a>
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="action" className="space-y-6">
              <Card className="glass-effect border-[hsl(var(--line))]">
                <CardHeader>
                  <CardTitle className="font-orbitron text-2xl">T300 Series In Action</CardTitle>
                  <CardDescription>
                    Watch the PUDU T300 Series demonstrate its capabilities in real-world environments
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="aspect-video w-full rounded-lg overflow-hidden bg-black/50">
                    <video 
                      controls 
                      className="w-full h-full object-contain"
                      poster={t300Img}
                      preload="metadata"
                      playsInline
                    >
                      <source src={actionVideo} type="video/quicktime" />
                      <source src={actionVideo} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  </div>
                  <div className="mt-6 text-center">
                    <p className="text-muted-foreground mb-4">
                      Experience the power of the T300 Series firsthand. See how it handles heavy payloads, navigates complex environments, and operates autonomously in real industrial settings.
                    </p>
                    <Button 
                      asChild 
                      size="lg"
                      className="glow-gold"
                    >
                      <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                        Schedule Your Demo
                      </a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

        <Footer />
    </>
  );
};

export default T300Series;
